import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  PersonAdd as PersonAddIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays } from 'date-fns';

import {
  createRepairOrder
} from '../redux/actions/repairOrderActions';
import {
  getActivePaymentModes
} from '../redux/actions/paymentModeActions';
import {
  getCustomers,
  createCustomer
} from '../redux/actions/customerActions';

const NewRepairOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { loading, error, success } = useSelector(state => state.repairOrders);
  const { activePaymentModes } = useSelector(state => state.paymentModes);
  const { customers } = useSelector(state => state.customers);

  // Form state
  const [formData, setFormData] = useState({
    customer_id: '',
    item_description: '',
    item_weight: '',
    item_type: '',
    repair_type: '',
    estimated_cost: '',
    delivery_date: addDays(new Date(), 7),
    notes: ''
  });

  // Customer dialog state
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: ''
  });

  // Payment state
  const [makeAdvancePayment, setMakeAdvancePayment] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_date: new Date(),
    payment_amount: '',
    notes: ''
  });
  const [selectedPaymentModes, setSelectedPaymentModes] = useState([]);

  // Payment mode icons mapping
  const paymentIcons = {
    cash: '💰',
    card: '💳',
    upi: '📱',
    netbanking: '🏦',
    cheque: '📋',
    neft: '🏛️',
    rtgs: '🏛️'
  };

  // Load payment modes and customers
  useEffect(() => {
    dispatch(getActivePaymentModes());
    dispatch(getCustomers());
  }, [dispatch]);

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle customer creation
  const handleCreateCustomer = async () => {
    try {
      const result = await dispatch(createCustomer(newCustomer));
      if (result.success) {
        // Refresh customer list
        dispatch(getCustomers());
        // Set the new customer as selected
        setFormData(prev => ({
          ...prev,
          customer_id: result.data.data.id
        }));
        setOpenCustomerDialog(false);
        // Reset new customer form
        setNewCustomer({
          name: '',
          phone: '',
          email: '',
          address: '',
          gstin: ''
        });
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  // Handle payment mode selection
  const handlePaymentModeToggle = (mode) => {
    setSelectedPaymentModes(prev => {
      const isSelected = prev.some(pm => pm.payment_mode_code === mode.code);
      if (isSelected) {
        return prev.filter(pm => pm.payment_mode_code !== mode.code);
      } else {
        return [...prev, {
          payment_mode_code: mode.code,
          amount: '',
          reference_number: '',
          bank_details: ''
        }];
      }
    });
  };

  const handlePaymentAmountChange = (modeCode, amount) => {
    setSelectedPaymentModes(prev =>
      prev.map(pm =>
        pm.payment_mode_code === modeCode
          ? { ...pm, amount }
          : pm
      )
    );
  };

  const handlePaymentReferenceChange = (modeCode, reference) => {
    setSelectedPaymentModes(prev =>
      prev.map(pm =>
        pm.payment_mode_code === modeCode
          ? { ...pm, reference_number: reference }
          : pm
      )
    );
  };

  const handlePaymentBankDetailsChange = (modeCode, bankDetails) => {
    setSelectedPaymentModes(prev =>
      prev.map(pm =>
        pm.payment_mode_code === modeCode
          ? { ...pm, bank_details: bankDetails }
          : pm
      )
    );
  };

  // Calculate total payment amount
  const getTotalPaymentAmount = () => {
    return selectedPaymentModes.reduce((total, pm) => total + parseFloat(pm.amount || 0), 0);
  };

  // Get selected customer
  const selectedCustomer = customers?.find(c => c.id === formData.customer_id);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.customer_id || !formData.item_description || !formData.estimated_cost) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate advance payment
    if (makeAdvancePayment && selectedPaymentModes.length === 0) {
      alert('Please select at least one payment mode for advance payment');
      return;
    }

    if (makeAdvancePayment) {
      const totalAmount = getTotalPaymentAmount();
      if (totalAmount !== parseFloat(formData.estimated_cost)) {
        if (totalAmount > parseFloat(formData.estimated_cost)) {
          alert('Advance payment cannot be more than estimated cost');
          return;
        }
      }
    }

    try {
      const submitData = {
        ...formData,
        estimated_cost: parseFloat(formData.estimated_cost),
        item_weight: parseFloat(formData.item_weight) || 0,
        delivery_date: format(formData.delivery_date, 'yyyy-MM-dd'),
        advance_amount: makeAdvancePayment ? getTotalPaymentAmount() : 0,
        payments: makeAdvancePayment ? selectedPaymentModes.map(pm => ({
          ...pm,
          amount: parseFloat(pm.amount),
          payment_date: format(paymentData.payment_date, 'yyyy-MM-dd')
        })) : []
      };

      const result = await dispatch(createRepairOrder(submitData));
      if (result.success) {
        navigate('/repair-orders');
      }
    } catch (error) {
      console.error('Error creating repair order:', error);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/repair-orders');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            New Repair Order
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              color="error"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                borderRadius: 2,
                px: 3,
                py: 1
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Repair Order'}
            </Button>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Panel - Form */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  Customer & Item Details
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  {/* Customer Selection */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Customer *</InputLabel>
                        <Select
                          value={formData.customer_id}
                          onChange={(e) => handleInputChange('customer_id', e.target.value)}
                          label="Customer *"
                        >
                          {customers?.map((customer) => (
                            <MenuItem key={customer.id} value={customer.id}>
                              {customer.name} - {customer.phone}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setOpenCustomerDialog(true)}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Add New
                      </Button>
                    </Box>
                    
                    {/* Selected Customer Details */}
                    {selectedCustomer && (
                      <Paper sx={{ p: 2, mt: 2, bgcolor: '#f8f9fa' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Selected Customer Details:
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Phone:</strong> {selectedCustomer.phone}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Email:</strong> {selectedCustomer.email || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2"><strong>Address:</strong> {selectedCustomer.address || 'N/A'}</Typography>
                          </Grid>
                          {selectedCustomer.gstin && (
                            <Grid item xs={12}>
                              <Typography variant="body2"><strong>GSTIN:</strong> {selectedCustomer.gstin}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    )}
                  </Grid>

                  {/* Item Description */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Item Description *"
                      multiline
                      rows={3}
                      value={formData.item_description}
                      onChange={(e) => handleInputChange('item_description', e.target.value)}
                      variant="outlined"
                      placeholder="Describe the item to be repaired..."
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Item Weight (grams)"
                      type="number"
                      value={formData.item_weight}
                      onChange={(e) => handleInputChange('item_weight', e.target.value)}
                      variant="outlined"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">g</InputAdornment>
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Item Type"
                      value={formData.item_type}
                      onChange={(e) => handleInputChange('item_type', e.target.value)}
                      variant="outlined"
                      placeholder="e.g., Gold Chain, Ring, Bracelet"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Repair Type"
                      value={formData.repair_type}
                      onChange={(e) => handleInputChange('repair_type', e.target.value)}
                      variant="outlined"
                      placeholder="e.g., Link Repair, Resizing, Polishing"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Estimated Cost *"
                      type="number"
                      value={formData.estimated_cost}
                      onChange={(e) => handleInputChange('estimated_cost', e.target.value)}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Expected Delivery Date"
                      value={formData.delivery_date}
                      onChange={(date) => handleInputChange('delivery_date', date)}
                      renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
                      minDate={new Date()}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={2}
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      variant="outlined"
                      placeholder="Additional notes or special instructions..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Advance Payment Section */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={makeAdvancePayment}
                      onChange={(e) => setMakeAdvancePayment(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Make Advance Payment
                    </Typography>
                  }
                />
                <Divider sx={{ my: 2 }} />

                {makeAdvancePayment && (
                  <Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Payment Date"
                          value={paymentData.payment_date}
                          onChange={(date) => setPaymentData(prev => ({ ...prev, payment_date: date }))}
                          renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Payment Notes"
                          value={paymentData.notes}
                          onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                          variant="outlined"
                          placeholder="Payment notes (optional)"
                        />
                      </Grid>
                    </Grid>

                    {/* Payment Modes Selection */}
                    <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
                      Select Payment Modes
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {activePaymentModes?.map((mode) => {
                        const isSelected = selectedPaymentModes.some(pm => pm.payment_mode_code === mode.code);
                        const selectedMode = selectedPaymentModes.find(pm => pm.payment_mode_code === mode.code);

                        return (
                          <Grid item xs={12} md={6} key={mode.id}>
                            <Accordion expanded={isSelected} onChange={() => handlePaymentModeToggle(mode)}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <Typography sx={{ fontSize: '1.5rem', mr: 2 }}>
                                    {paymentIcons[mode.icon] || '💳'}
                                  </Typography>
                                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                    {mode.name}
                                  </Typography>
                                  {isSelected && (
                                    <Chip label="Selected" color="primary" size="small" />
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Amount"
                                      type="number"
                                      value={selectedMode?.amount || ''}
                                      onChange={(e) => handlePaymentAmountChange(mode.code, e.target.value)}
                                      variant="outlined"
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Reference Number"
                                      value={selectedMode?.reference_number || ''}
                                      onChange={(e) => handlePaymentReferenceChange(mode.code, e.target.value)}
                                      variant="outlined"
                                      placeholder="Transaction ID, Check No., etc."
                                    />
                                  </Grid>
                                  {mode.code !== 'cash' && (
                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        label="Bank Details"
                                        multiline
                                        rows={2}
                                        value={selectedMode?.bank_details || ''}
                                        onChange={(e) => handlePaymentBankDetailsChange(mode.code, e.target.value)}
                                        variant="outlined"
                                        placeholder="Bank name, account details, etc."
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel - Summary */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 20 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  Repair Order Summary
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Customer:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedCustomer?.name || 'Not selected'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Estimated Cost:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ₹{formData.estimated_cost || '0.00'}
                    </Typography>
                  </Box>

                  {makeAdvancePayment && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">Advance Payment:</Typography>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          ₹{getTotalPaymentAmount().toFixed(2)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">Balance Amount:</Typography>
                        <Typography variant="body2" fontWeight="medium" color="warning.main">
                          ₹{(parseFloat(formData.estimated_cost || 0) - getTotalPaymentAmount()).toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Delivery Date:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formData.delivery_date ? format(formData.delivery_date, 'dd-MM-yyyy') : 'Not set'}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total Amount:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      ₹{formData.estimated_cost || '0.00'}
                    </Typography>
                  </Box>

                  {makeAdvancePayment && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Payment Status: {getTotalPaymentAmount() === parseFloat(formData.estimated_cost || 0) ? 'Fully Paid' : 'Partially Paid'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Add Customer Dialog */}
        <Dialog open={openCustomerDialog} onClose={() => setOpenCustomerDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ background: 'linear-gradient(45deg, #1976d2, #42a5f5)', color: 'white' }}>
            Add New Customer
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Name *"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number *"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="GSTIN"
                  value={newCustomer.gstin}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, gstin: e.target.value }))}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCustomerDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomer} variant="contained">
              Create Customer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default NewRepairOrder;

